import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { X, FolderPlus, Folder as FolderIcon, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Small warm sheet for picking or creating a personal folder for a save.
 * Folders are private — only the owner can see them.
 */
export default function FolderPicker({ open, ideaId, currentFolderId, onClose, onSaved }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/folders").then(({ data }) => setFolders(data.folders || [])).finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const assign = async (folder_id) => {
    try {
      await api.post(`/ideas/${ideaId}/save`, { folder_id });
      onSaved?.(folder_id);
      toast(folder_id ? "Filed away." : "Saved to Your Grove.");
      onClose?.();
    } catch { toast("Could not save."); }
  };

  const createAndAssign = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/folders", { name });
      await assign(data.id);
    } catch (e) { toast(e?.response?.data?.detail || "Could not create folder."); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#2A2823]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog" aria-modal="true" data-testid="folder-picker" onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF8F1] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-[#B79256]/30 shadow-[0_30px_80px_rgb(42,40,35,0.2)] overflow-hidden max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#B79256]/25">
          <div>
            <div className="accent-label text-[#B79256]">file this branch</div>
            <div className="text-xs text-[#2A2823]/60 mt-1">Folders stay private to you.</div>
          </div>
          <button onClick={onClose} data-testid="folder-picker-close" aria-label="Close" className="p-2 rounded-full hover:bg-[#B79256]/15 text-[#2A2823]/60 hover:text-[#2A2823]">
            <X className="w-4 h-4" strokeWidth={1.75}/>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <button
            type="button"
            data-testid="folder-none"
            onClick={() => assign(null)}
            className={`w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[#B79256]/8 transition-colors ${!currentFolderId ? "bg-[#4E5A3D]/6" : ""}`}
          >
            <FolderIcon className="w-4 h-4 text-[#B79256]" strokeWidth={1.75}/>
            <span className="flex-1 text-[#2A2823]">No folder — just Your Grove</span>
            {!currentFolderId && <Check className="w-4 h-4 text-[#4E5A3D]" strokeWidth={2}/>}
          </button>

          {loading ? (
            <div className="px-5 py-4 text-[#2A2823]/50 text-sm">Loading folders…</div>
          ) : folders.length === 0 ? (
            <div className="px-5 py-4 text-[#2A2823]/50 text-sm italic">No folders yet — create one below.</div>
          ) : (
            <ul>
              {folders.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    data-testid={`folder-pick-${f.id}`}
                    onClick={() => assign(f.id)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[#B79256]/8 transition-colors ${currentFolderId === f.id ? "bg-[#4E5A3D]/6" : ""}`}
                  >
                    <FolderIcon className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75}/>
                    <span className="flex-1 text-[#2A2823]">{f.name}</span>
                    <span className="text-xs text-[#2A2823]/50">{f.count || 0}</span>
                    {currentFolderId === f.id && <Check className="w-4 h-4 text-[#4E5A3D] ml-1" strokeWidth={2}/>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-[#B79256]/25 p-4">
          {creating ? (
            <div className="flex gap-2">
              <input
                data-testid="folder-new-name"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createAndAssign()}
                placeholder="e.g. Weeknight Dinners, Christmas Ideas, Someday"
                className="flex-1 h-11 px-4 rounded-full border border-[#B79256]/40 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E5A3D] text-sm"
              />
              <button
                type="button"
                data-testid="folder-new-create"
                onClick={createAndAssign}
                className="h-11 px-5 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] text-sm"
              >
                Create
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="folder-new-open"
              onClick={() => setCreating(true)}
              className="w-full h-11 rounded-full border-2 border-dashed border-[#4E5A3D]/40 text-[#4E5A3D] hover:bg-[#4E5A3D]/8 transition-colors inline-flex items-center justify-center gap-2 font-medium"
            >
              <FolderPlus className="w-4 h-4" strokeWidth={1.75}/> New folder
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
